import { Product } from "@/core/entities/product";
import { useGetProducts } from "@/hooks/repository-hooks/product/use-product";
import { useEffect } from "react";
import { create } from "zustand";

interface ProductsStore {
  products: Product[];
  isLoading: boolean;
  error: string | null;
  setProducts: (products: Product[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  getProductById: (id: string) => Product | undefined;
  getProductName: (id: string) => string;
}

export const useProductsStore = create<ProductsStore>((set, get) => ({
  products: [],
  isLoading: false,
  error: null,
  setProducts: (products) => set({ products, error: null }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error, isLoading: false }),
  getProductById: (id) => {
    const { products } = get();
    return products.find((product) => product.id === id);
  },
  getProductName: (id) => {
    const { products } = get();
    const product = products.find((product) => product.id === id);
    return product?.name ?? id;
  },
}));

// Hook to initialize products data
export const useInitializeProducts = () => {
  const { data, isLoading, error } = useGetProducts();
  const { setProducts, setLoading, setError } = useProductsStore();

  // Update store when data changes
  useEffect(() => {
    setLoading(isLoading);

    if (error) {
      setError(error.message || "Failed to fetch products");
    } else if (data) {
      setProducts(data);
    }
  }, [data, isLoading, error, setProducts, setLoading, setError]);

  return { isLoading, error };
};
